/**
 * search_zero_state.js
 * --------------------
 * CL-22: the starting points shown in the nav search overlay before the member
 * types: their recent searches, then popular stores and quick links.
 * Rendering only — shop_nav_wiring.js decides when it shows, handles the
 * keyboard, saves recent searches, and sends analytics.
 *
 * Built with createElement/textContent (SHOPWEB-1369: no innerHTML sinks).
 * Pills reuse fragno's .fragno-global-nav__chip styling so they match the
 * design system.
 */

const ITEM_CLASS = "shop-search-zero-state__item";
const CLEAR_CLASS = "shop-search-zero-state__clear";
const LIST_ID = "shop-search-zero-state-list";
// Design: pill text longer than this is cut and ends in an ellipsis, so a long
// search or store name cannot overflow its pill, especially on small screens.
export const MAX_PILL_TEXT_LENGTH = 30;

export function truncatePillText(text) {
  const chars = Array.from(text);
  if (chars.length <= MAX_PILL_TEXT_LENGTH) return text;
  return `${chars.slice(0, MAX_PILL_TEXT_LENGTH).join("").trimEnd()}…`;
}

// clock-rewind from the Figma (Global Nav v1.0), path data unchanged. Stroked
// with currentColor so it follows the pill's text color (#424242 in the design).
const CLOCK_REWIND_PATH =
  "M18.9166 11.25L17.2505 9.58333L15.5833 11.25M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C12.7516 2.5 15.1571 3.98178 16.4621 6.19091M10 5.83333V10L12.5 11.6667";

// Same-site paths only. Items can come from config or be built from saved
// recent searches, so never trust an href as-is.
// A leading "/" is not enough: browsers read "/\evil.com" and "/<tab>/evil.com"
// as "//evil.com". So resolve the href the way the browser will, require the
// same origin, and return the normalized path (or null) rather than the raw
// string, so what we render is exactly what we checked. Dot segments can
// collapse a same-site path to "//host" ("/.//evil.com"), which would be
// protocol-relative once rendered as an href, so that form is rejected too.
export function safeHref(href) {
  if (typeof href !== "string" || !href.startsWith("/")) return null;
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    const normalized = url.pathname + url.search + url.hash;
    return normalized.startsWith("//") ? null : normalized;
  } catch {
    return null;
  }
}

export function isSafeHref(href) {
  return safeHref(href) !== null;
}

function cleanItems(items) {
  if (!Array.isArray(items)) return [];
  return items.flatMap((item) => {
    if (!item || typeof item.text !== "string" || !item.text.trim()) return [];
    const href = safeHref(item.href);
    return href ? [{ ...item, href }] : [];
  });
}

/**
 * Parse the header's data-zero-state JSON. Returns null when the attribute is
 * missing or malformed, which means the feature is off.
 *
 * recentScope is who the saved recent searches belong to (an opaque value from
 * the server). Without one, recent searches are switched off rather than
 * guessed at, so they can never be mixed between people.
 */
export function parseZeroStateConfig(json) {
  if (!json) return null;
  try {
    const data = JSON.parse(json);
    if (!data || typeof data !== "object") return null;
    const scope = data.recent_scope;
    return {
      popular: cleanItems(data.popular),
      quickLinks: cleanItems(data.quick_links),
      labels: data.labels || {},
      recentScope:
        typeof scope === "string" && scope.length > 0 && scope.length <= 64
          ? scope
          : null,
    };
  } catch {
    return null;
  }
}

function buildClockIcon() {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("class", "shop-search-zero-state__icon");
  svg.setAttribute("width", "20");
  svg.setAttribute("height", "20");
  svg.setAttribute("viewBox", "0 0 20 20");
  svg.setAttribute("fill", "none");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", CLOCK_REWIND_PATH);
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "1.66667");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.appendChild(path);
  return svg;
}

// A labelled section of pills. Each item carries its `source` (recent search,
// popular store, quick link) so analytics can tell them apart even when the
// design shows them in one list. `position` counts within the section, from 1.
function buildSection({ id, heading, items, icon }) {
  const section = document.createElement("div");
  section.className = "shop-search-zero-state__group";
  section.setAttribute("role", "group");

  // fragno's own heading style is the design's section label (Text md/Medium,
  // gray-800, 16px above the pills), so reuse it rather than restyling.
  const headingEl = document.createElement("p");
  headingEl.className =
    "fragno-global-nav__suggested-heading shop-search-zero-state__heading";
  headingEl.id = `shop-search-zero-state-${id}-heading`;
  headingEl.textContent = heading;
  section.setAttribute("aria-labelledby", headingEl.id);
  section.appendChild(headingEl);

  const list = document.createElement("div");
  list.className = "shop-search-zero-state__items";
  // Layout-only wrapper between role="group" and role="option": presentational,
  // so the listbox visibly owns its options through the group.
  list.setAttribute("role", "presentation");
  const seen = {};
  items.forEach((item, i) => {
    seen[item.source] = (seen[item.source] || 0) + 1;
    const a = document.createElement("a");
    a.className = `fragno-global-nav__chip ${ITEM_CLASS}`;
    a.id = `shop-search-zero-state-${item.source}-${seen[item.source] - 1}`;
    a.setAttribute("role", "option");
    a.setAttribute("href", item.href);
    a.dataset.group = item.source;
    a.dataset.position = String(i + 1);
    const full = item.text.trim();
    const text = truncatePillText(full);
    if (text !== full) {
      // The pill shows a shortened copy, so keep the whole text for hover and
      // for assistive tech. It starts with the visible text.
      a.title = full;
      a.setAttribute("aria-label", full);
    }
    const label = document.createElement("span");
    label.className = "shop-search-zero-state__text";
    label.textContent = text;
    if (icon) a.append(buildClockIcon(), label);
    else a.appendChild(label);
    list.appendChild(a);
  });
  section.appendChild(list);

  return section;
}

export function createZeroState({ labels = {} } = {}) {
  // The root holds the listbox and, when there are recent searches, the Clear
  // button. The button sits beside the listbox rather than in it: a listbox
  // may only own options, and interactive content inside one confuses screen
  // readers. It is placed in the "Recent searches" heading row with CSS.
  const el = document.createElement("div");
  el.className = "shop-search-zero-state";
  el.hidden = true;

  // The search input points at this list with aria-controls (see shop_nav_wiring.js).
  const listEl = document.createElement("div");
  listEl.className = "shop-search-zero-state__list";
  listEl.id = LIST_ID;
  listEl.setAttribute("role", "listbox");
  listEl.setAttribute("aria-label", "Search suggestions");
  el.appendChild(listEl);

  function buildClearButton() {
    const button = document.createElement("button");
    button.type = "button";
    button.className = CLEAR_CLASS;
    button.textContent = labels.clear || "Clear recent";
    // The name keeps the visible word first ("Clear ...") so it still matches
    // what sighted members and voice-control users see.
    button.setAttribute(
      "aria-label",
      labels.clear_aria || "Clear recent searches",
    );
    return button;
  }

  // Figma (Global Nav v1.0): "Recent searches" (only when the member has any)
  // above one "Suggested for you" section, popular stores first, then quick
  // links.
  function render({ recent = [], popular = [], quickLinks = [] } = {}) {
    const recentItems = cleanItems(recent).map((item) => ({
      ...item,
      source: "recent",
    }));
    const suggested = [
      ...cleanItems(popular).map((item) => ({ ...item, source: "popular" })),
      ...cleanItems(quickLinks).map((item) => ({
        ...item,
        source: "quick_link",
      })),
    ];
    const sections = [
      {
        id: "recent",
        heading: labels.recent || "Recent searches",
        items: recentItems,
        icon: true,
      },
      {
        id: "suggested",
        heading: labels.suggested || "Suggested for you",
        items: suggested,
      },
    ];
    listEl.replaceChildren(
      ...sections
        .filter((section) => section.items.length > 0)
        .map(buildSection),
    );
    el.replaceChildren(
      ...(recentItems.length > 0 ? [buildClearButton()] : []),
      listEl,
    );
  }

  function items() {
    return Array.from(listEl.querySelectorAll(`.${ITEM_CLASS}`));
  }

  function counts() {
    const tally = { recent: 0, popular: 0, quick_link: 0 };
    items().forEach((item) => {
      tally[item.dataset.group] = (tally[item.dataset.group] || 0) + 1;
    });
    return tally;
  }

  return {
    el,
    listEl,
    render,
    items,
    counts,
    hasItems: () => items().length > 0,
  };
}

export function isZeroStateItem(el) {
  return el?.classList?.contains(ITEM_CLASS) ?? false;
}

export function isZeroStateClear(el) {
  return el?.classList?.contains(CLEAR_CLASS) ?? false;
}
